import React, { useState, useEffect, useRef } from "react";
import { useSelector, useStore } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  Box,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import LoadableButton from "src/components/LoadableButton";

import { getAllSales } from "src/store/sale/selector";
import { transactionsSelector } from "src/store/transaction/selector";
import {
  effectiveUserTimeZoneSelector,
  selectUser,
  userPlanSelector,
  userGetInventoryTypeSelector,
} from "src/store/system/selector";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import {
  fetchBalanceSheetReport,
  fetchInventoryReport,
} from "src/apiService/modules/reports";
import {
  calendarYmdInTimeZone,
  getDateTimePartsInTimeZone,
  zonedMonthRangeFromZeroIndexedMonth,
} from "src/utils/timezone";
import { USER_PLAN } from "src/enums/common.enum";

import ParametersSelection from "./ParametersSelection";
import {
  ReportTypes,
  reportIsInventory,
  reportIsPlatformTransaction,
  buildPlatformTransactionReport,
  reportIsConsignmentCommission,
  buildConsignmentCommissionReport,
  buildScheduleCReport,
  reportIsScheduleC,
  reportIsBalanceSheet,
  ReportParameters,
} from "./report";
import {
  type ReportRunResult,
  errorMessageFromUnknown,
  isLiveReportRunSuccess,
  isReportRunError,
  isReportRunLoading,
  isReportRunSuccess,
  isSnapshotReportRunSuccess,
  markReportRunStale,
} from "./reportRunResult";
import {
  LiveProfitLossComparisonReportView,
  LiveProfitLossReportView,
  LiveSalesTaxReportView,
} from "./LiveReportViews";
import PlatformTransactionReport from "./PlatformTransactionReport";
import ConsignmentCommissionReport from "./ConsignmentCommissionReport";
import ScheduleCReport from "./ScheduleCReport";
import BalanceSheetReport from "./BalanceSheetReport";
import InventoryReport from "./InventoryReport";
import { useSetManualLoadedFromDate } from "./useSetManualLoadedFromDate";

/** Reports that do not read sales/transactions from the client listener cache. */
const ReportTypesSkipManualLoad: ReadonlySet<ReportTypes> = new Set([
  "balance-sheet",
  "inventory",
  "schedule-c",
]);

function createInitialReportParameters(
  searchParams: URLSearchParams,
  timeZone: string
): ReportParameters {
  const initialStartDate = searchParams.get("startDate");
  const initialEndDate = searchParams.get("endDate");

  if (initialStartDate && initialEndDate) {
    const startDate = new Date(initialStartDate);
    const endDate = new Date(initialEndDate);
    const year = getDateTimePartsInTimeZone(endDate, timeZone).year;
    return {
      startDate,
      endDate,
      year,
      date: endDate,
    };
  }

  if (initialStartDate) {
    const startDate = new Date(initialStartDate);
    const { year, month } = getDateTimePartsInTimeZone(startDate, timeZone);
    const { start, end } = zonedMonthRangeFromZeroIndexedMonth(
      year,
      month - 1,
      timeZone
    );
    return { startDate: start, endDate: end, year, date: end };
  }

  if (initialEndDate) {
    const endDate = new Date(initialEndDate);
    const { year, month } = getDateTimePartsInTimeZone(endDate, timeZone);
    const { start, end } = zonedMonthRangeFromZeroIndexedMonth(
      year,
      month - 1,
      timeZone
    );
    return { startDate: start, endDate: end, year, date: end };
  }

  const now = new Date();
  const { year, month } = getDateTimePartsInTimeZone(now, timeZone);
  const { start, end } = zonedMonthRangeFromZeroIndexedMonth(
    year,
    month - 1,
    timeZone
  );

  return {
    startDate: start,
    endDate: end,
    year,
    date: end,
  };
}

export function Content() {
  const recordEvent = useRecordEvent();

  const store = useStore();
  const location = useLocation();
  const userPlan = useSelector(userPlanSelector);
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const setManualLoadedFromDate = useSetManualLoadedFromDate();
  const hasAutoRun = useRef(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  const shouldScrollOnSuccessRef = useRef(false);

  // Parse URL search params for initial state
  const searchParams = new URLSearchParams(location.search);
  const initialReportType = searchParams.get(
    "reportType"
  ) as ReportTypes | null;
  const initialAutoRun = searchParams.get("autoRun") === "true";

  const [parameters, setParameters] = useState<ReportParameters>(() =>
    createInitialReportParameters(
      new URLSearchParams(location.search),
      timeZone
    )
  );
  const [reportType, setReportType] = useState<ReportTypes>(
    initialReportType || "profit/loss"
  );
  const [result, setResult] = useState<ReportRunResult>({ status: "idle" });
  const [reportKey, setReportKey] = useState(0);
  const inventoryTypeIsCash =
    useSelector(userGetInventoryTypeSelector) === "cash";

  const generateReport = async () => {
    const rt = reportType;
    if (!ReportTypesSkipManualLoad.has(rt)) {
      const manualLoadStartDate =
        rt === "profit/loss" &&
        parameters.compareStartDate &&
        parameters.compareStartDate < parameters.startDate
          ? parameters.compareStartDate
          : parameters.startDate;
      await setManualLoadedFromDate(manualLoadStartDate, {
        onBeforeFirestoreWait: () =>
          setResult({ status: "loading", reportType: rt }),
      });
    }
    const state = store.getState();
    const sales = getAllSales(state);
    const user = selectUser(state);
    const timeZone = effectiveUserTimeZoneSelector(state);
    // Intentional Run only — remount + scroll; drill edits must not call this.
    setReportKey((k) => k + 1);
    shouldScrollOnSuccessRef.current = true;
    recordEvent({
      type: "feature-adoption",
      feature: "report-run",
      reportType: rt,
      periodParameters: parameters,
    });

    switch (rt) {
      case "balance-sheet": {
        setResult({ status: "loading", reportType: rt });
        try {
          const report = await fetchBalanceSheetReport(
            calendarYmdInTimeZone(parameters.date, timeZone)
          );
          setResult({ status: "success", report });
        } catch (e: unknown) {
          setResult({
            status: "error",
            reportType: rt,
            message: errorMessageFromUnknown(e),
          });
        }
        break;
      }

      case "profit/loss":
        setResult({
          status: "success",
          reportType: "profit/loss",
          parameters: { ...parameters },
        });
        break;

      case "profit/loss-comparison":
        setResult({
          status: "success",
          reportType: "profit/loss-comparison",
          parameters: {
            ...parameters,
            groupBy: parameters.groupBy || "month",
          },
        });
        break;

      case "inventory": {
        setResult({ status: "loading", reportType: rt });
        try {
          const report = await fetchInventoryReport(parameters.year);
          setResult({ status: "success", report });
        } catch (e: unknown) {
          setResult({
            status: "error",
            reportType: rt,
            message: errorMessageFromUnknown(e),
          });
        }
        break;
      }

      case "platform-transaction":
        setResult({
          status: "success",
          report: buildPlatformTransactionReport(
            rt,
            parameters,
            sales,
            transactionsSelector(state),
            user
          ),
        });
        break;

      case "consignment-commission":
        setResult({
          status: "success",
          report: buildConsignmentCommissionReport(rt, parameters, sales, user),
        });
        break;

      case "sales-tax":
        setResult({
          status: "success",
          reportType: "sales-tax",
          parameters: { ...parameters },
        });
        break;

      case "schedule-c":
        setResult({
          status: "success",
          report: buildScheduleCReport(rt, parameters),
        });
        break;
    }
  };

  // Auto-run report if navigated from Overview cards
  useEffect(() => {
    if (initialAutoRun && !hasAutoRun.current) {
      hasAutoRun.current = true;
      generateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot autoRun; generateReport closes over latest state
  }, [initialAutoRun]);

  useEffect(() => {
    if (!shouldScrollOnSuccessRef.current || !isReportRunSuccess(result))
      return;
    const timeout = setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      shouldScrollOnSuccessRef.current = false;
    }, 100);
    return () => clearTimeout(timeout);
  }, [result]);

  const displayResults = () => {
    if (!isReportRunSuccess(result)) return null;

    if (isLiveReportRunSuccess(result)) {
      switch (result.reportType) {
        case "profit/loss":
          return <LiveProfitLossReportView parameters={result.parameters} />;
        case "profit/loss-comparison":
          return (
            <LiveProfitLossComparisonReportView
              parameters={result.parameters}
              onGroupByChange={(groupBy) => {
                // Update form + frozen run params — no remount; live selector recomputes.
                setParameters((p) => ({ ...p, groupBy }));
                setResult((current) =>
                  isLiveReportRunSuccess(current) &&
                  current.reportType === "profit/loss-comparison"
                    ? {
                        ...current,
                        parameters: { ...current.parameters, groupBy },
                      }
                    : current
                );
              }}
            />
          );
        case "sales-tax":
          return <LiveSalesTaxReportView parameters={result.parameters} />;
      }
    }

    if (!isSnapshotReportRunSuccess(result)) return null;

    const reportData = result.report;

    if (reportIsInventory(reportData)) {
      return (
        <InventoryReport
          results={reportData}
          showStaleReportWarning={!!result.staleReportWarning}
          onRerunReport={() => generateReport()}
          onReportDataMutated={() =>
            setResult((current) => markReportRunStale(current))
          }
        />
      );
    }

    if (reportIsPlatformTransaction(reportData)) {
      return (
        <PlatformTransactionReport
          report={reportData}
          showStaleReportWarning={!!result.staleReportWarning}
          onRerunReport={() => generateReport()}
          onReportDataMutated={() =>
            setResult((current) => markReportRunStale(current))
          }
        />
      );
    }

    if (reportIsConsignmentCommission(reportData)) {
      return <ConsignmentCommissionReport report={reportData} />;
    }

    if (reportIsScheduleC(reportData)) {
      return (
        <ScheduleCReport
          report={reportData}
          onClearReport={() => setResult({ status: "idle" })}
        />
      );
    }

    if (reportIsBalanceSheet(reportData)) {
      return (
        <BalanceSheetReport
          report={reportData}
          showStaleReportWarning={!!result.staleReportWarning}
          onRerunReport={() => generateReport()}
          onReportDataMutated={() =>
            setResult((current) => markReportRunStale(current))
          }
        />
      );
    }

    return null;
  };

  /** Async reports set `loading` / `error` with `reportType`; match UI to the selected report. */
  const reportRunLoading =
    isReportRunLoading(result) && result.reportType === reportType;
  const reportRunErrorMessage =
    isReportRunError(result) && result.reportType === reportType
      ? result.message
      : null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper className="p-5 h-auto mt-4">
          <Box position="relative">
            <Box>
              <FormControl variant="outlined" margin="dense">
                <InputLabel id="start-month">Report Type</InputLabel>
                <Select
                  margin="dense"
                  label="Report Type"
                  value={reportType}
                  onChange={(e: React.ChangeEvent<any>) => {
                    setReportType(e.target.value);
                    setResult({ status: "idle" });
                  }}
                  id={"report-type-dropdown"}
                >
                  <MenuItem value="profit/loss">Profit/Loss Statement</MenuItem>
                  <MenuItem value="profit/loss-comparison">
                    Profit/Loss Comparison
                  </MenuItem>
                  <MenuItem value="balance-sheet">Balance Sheet</MenuItem>
                  {inventoryTypeIsCash ? null : (
                    <MenuItem value="inventory">Inventory Report</MenuItem>
                  )}
                  <MenuItem value="platform-transaction">
                    Platform Transactions
                  </MenuItem>
                  {userPlan && userPlan !== USER_PLAN.BASIC ? (
                    <MenuItem value="consignment-commission">
                      Consignment Commission
                    </MenuItem>
                  ) : null}
                  <MenuItem value="sales-tax">Sales Tax Report</MenuItem>
                  <MenuItem value="schedule-c">Schedule C Generator</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <ParametersSelection
              parameters={parameters}
              reportType={reportType}
              setParameters={setParameters}
            />
          </Box>
          {reportRunErrorMessage ? (
            <Box mt={2}>
              <Alert severity="error">{reportRunErrorMessage}</Alert>
            </Box>
          ) : null}
          <LoadableButton
            id={"run-report-button"}
            color="primary"
            variant="contained"
            loading={reportRunLoading}
            disabled={
              (reportType === "platform-transaction" && !parameters.platform) ||
              (reportType === "consignment-commission" && !parameters.consignor)
            }
            onClick={() => generateReport()}
            className="mt-5"
          >
            Run Report
          </LoadableButton>
          {reportRunLoading ? (
            <Box mt={2}>
              <LinearProgress />
              <Box mt={1} fontSize="caption" color="textSecondary">
                Loading data for this report period…
              </Box>
            </Box>
          ) : null}
        </Paper>
        <div ref={resultsRef}>
          {isReportRunSuccess(result) && (
            <Fade key={reportKey} in={true} timeout={400}>
              <div>{displayResults()}</div>
            </Fade>
          )}
        </div>
      </Grid>
    </Grid>
  );
}
